import { MessageSourceProps, withMessages } from 'react-message-source';
import axios, { CancelTokenSource } from 'axios';
import React, { Component } from 'react';
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import PxGrid from 'components/layout/PxGrid';
import { Button, Grid, Modal } from 'semantic-ui-react';
import Input from 'components/final-form/Input';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import { CheckEmailDto, EmployeeDto } from 'ts-types/api.types';
import { findAllByCompanyEmail } from 'services/employeeServices';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import { withAuthContext } from 'auth/AuthContext';
import styled from 'styled-components';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import LoaderComponent from 'components/LoaderComponent';
import { FormApi, FormState } from 'final-form';
import { debounce } from 'ts-debounce';
import { noop } from 'utils/functionUtils';

const StyledContainer = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const StyledTableRow = styled(StyledViewTableRow)`
   flex: 1 0 300px;
`;

interface CheckEmailFieldProps extends FormState {
}

interface Props extends MessageSourceProps {
  onCancel: () => void,
  toggleCheckEmailModal: () => void,
}

interface State {
  foundEmployees: Array<CheckEmailDto>,
  employeesLoaded: boolean,
  cancelTokenSource: CancelTokenSource,
}

class CheckEmailModal extends Component<Props, State> {

  private mounted: boolean = false;

  private initialSearchValues: any = {
    companyEmail: ""
  };

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      foundEmployees: [],
      employeesLoaded: true,
      cancelTokenSource
    };
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource && cancelTokenSource.cancel('Operation canceled by user');
  }

  handleSubmit = async (values: any) => {

    this.setState({
      employeesLoaded: false
    });

    if (values.companyEmail) {
      const foundEmployees = await findAllByCompanyEmail(values.companyEmail, this.state.cancelTokenSource)

      this.setState({
        foundEmployees: foundEmployees
      });
    }
    this.setEmployeesLoadingFinished();
  }

  setEmployeesLoadingFinished = (): void => {
    this.setState({
      employeesLoaded: true
    });
  }

  handleChange = debounce(({values}: CheckEmailFieldProps, form: FormApi) => {

    const {cancelTokenSource} = this.state;

    if(!this.mounted) {
      return;
    }

    this.setState({employeesLoaded: false});

    findAllByCompanyEmail(values.companyEmail, cancelTokenSource)
        .then(response =>
            this.setState({
              foundEmployees: response
            })
        )
        .catch(noop)
        .finally(() => this.setState({
          employeesLoaded: true
        }));
  },300)

  render(): React.ReactNode {

    const {getMessage, onCancel} = this.props;
    const {employeesLoaded} = this.state;

    return (
        <StyledContainer style={{height: "100%"}}>
          <div>
            {this.renderSearchEmailSegment()}
          </div>
          <StyledTableRow>
            {!employeesLoaded
                ? <LoaderComponent message={getMessage("employee.loading")}/>
                : this.renderEmployeesSegment()}
          </StyledTableRow>
          <FormButtonsRow>
            <Button
                compact
                type="button"
                floated={"left"}
                onClick={onCancel}
            >
              {getMessage("button.close")}
            </Button>
          </FormButtonsRow>
        </StyledContainer>
    );
  }

  renderSearchEmailSegment = (): JSX.Element => {
    return (
        <FinalForm
            onSubmit={this.handleSubmit}
            initialValues={this.initialSearchValues}
            subscription={{pristine: true, submitting: true}}
            render={this.renderSearchForm}
        />
    );
  };

  renderSearchForm = ({handleSubmit, submitting, form}: FormRenderProps): React.ReactNode => {

    const {getMessage} = this.props;

    return (
        <form onSubmit={handleSubmit}>
          <PxGrid columns={2}>
            <Grid.Row>
              <Grid.Column width={3} verticalAlign="middle">
                {getMessage("employee.companyEmail")}:
              </Grid.Column>
              <Grid.Column width={10}>
                <Field
                    name="companyEmail"
                    component={Input}
                    autoFocus
                    fluid
                />
              </Grid.Column>
              <Grid.Column width={3}>
                <Button
                    type={"submit"}
                    compact
                    primary
                    disabled={submitting}
                >
                  {getMessage("search")}
                </Button>
              </Grid.Column>
            </Grid.Row>
            <FormSpy subscription={{values: true}} onChange={formState => this.handleChange(formState, form)}/>
          </PxGrid>
        </form>
    )
  };

  employeesEmailRowGetter = ({index}: any) => {
    const {foundEmployees} = this.state;

    Object.assign(foundEmployees[index], {index: index + 1});

    return foundEmployees[index];
  };

  employeesEmailRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
        >
          {columns}
        </div>
    );
  };

  renderEmployeesSegment = (): JSX.Element => {

    const {getMessage} = this.props;
    const {foundEmployees} = this.state;

    const tableColumns = [
      {
        width: 50,
        label: "#",
        dataKey: "index"
      },
      {
        width: 150,
        label: (getMessage("employee.title")),
        dataKey: "firstName",
        cellRenderer: this.employeeCellRenderer
      },
      {
        width: 300,
        label: (getMessage("employee.companyEmail")),
        dataKey: "companyEmail"
      },
      {
        width: 250,
        label: (getMessage("employee.companyId")),
        dataKey: "companyName"
      },
      {
        width: 100,
        label: (getMessage("employee.deleted")),
        dataKey: "deleted",
        cellRenderer: this.deletedCellRenderer
      },
      {
        width: 150,
        label: (getMessage("employee.deletedCompany")),
        dataKey: "companyDeleted",
        cellRenderer: this.deletedCellRenderer
      }
    ];

    return (
        <VirtualizedTable
            rowCount={foundEmployees.length}
            rowGetter={this.employeesEmailRowGetter}
            rowRenderer={this.employeesEmailRowRenderer}
            columns={tableColumns}
        />

    );
  };

  deletedCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;

    if (cellData) {
      return getMessage("employee.yes")
    }
    return getMessage("employee.no")
  }

  employeeCellRenderer = ({rowData}: any) => {

    const trigger = (
        <div>{rowData.firstName} {rowData.name}</div>
    );

    return trigger;
  }
}

export default withAuthContext(withMessages(CheckEmailModal));
